import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { FileText, Trash2, Upload, CheckCircle, Clock, XCircle, ArrowRight, Star, AlertTriangle, RotateCcw, Pencil, UserX } from 'lucide-react';
import StarRating from '@/components/StarRating';
import Navbar from '@/components/Navbar';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  SAVED: 'bg-amber-50 text-amber-700 border-amber-200',
  IN_VALIDATION: 'bg-blue-50 text-blue-700 border-blue-200',
  CORRECTION_REQUIRED: 'bg-orange-50 text-orange-700 border-orange-200',
  VALIDATED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  EXPIRED: 'bg-muted text-muted-foreground border-border',
};

const STATUS_ICONS = {
  SAVED: <Clock className="w-3 h-3 mr-1" />,
  IN_VALIDATION: <FileText className="w-3 h-3 mr-1" />,
  CORRECTION_REQUIRED: <AlertTriangle className="w-3 h-3 mr-1" />,
  VALIDATED: <CheckCircle className="w-3 h-3 mr-1" />,
  EXPIRED: <XCircle className="w-3 h-3 mr-1" />,
};

export default function MyReviews() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  // Completion dialog state
  const [completing, setCompleting] = useState(null);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [receiptAmount, setReceiptAmount] = useState('');
  const [coinsUsed, setCoinsUsed] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  // Correction dialog state
  const [correcting, setCorrecting] = useState(null); // review object
  const [correctText, setCorrectText] = useState('');
  const [correctRating, setCorrectRating] = useState(0);
  const [correctAmount, setCorrectAmount] = useState('');
  const [correctCoins, setCorrectCoins] = useState('');
  const [correcting_busy, setCorrectingBusy] = useState(false);
  // Edit-draft dialog state (SAVED only)
  const [editingDraft, setEditingDraft] = useState(null);
  const [editText, setEditText] = useState('');
  const [editRating, setEditRating] = useState(0);
  const [editingBusy, setEditingBusy] = useState(false);
  // Referral-inactive dialog (Phase 20)
  const [inactiveReferral, setInactiveReferral] = useState(null); // {review, source: 'complete'|'correct'}

  const fetchReviews = async () => {
    try {
      const [revRes, walletRes] = await Promise.all([
        axios.get(`${API}/reviews/my`, { withCredentials: true }),
        axios.get(`${API}/wallet`, { withCredentials: true }).catch(() => ({ data: { balance: 0 } }))
      ]);
      setReviews(revRes.data);
      setWalletBalance(walletRes.data.balance || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReviews(); }, []);

  const handleDelete = async (reviewId) => {
    try {
      await axios.delete(`${API}/drafts/${reviewId}`, { withCredentials: true });
      toast.success(t('mr_delete') + ' OK');
      fetchReviews();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openComplete = (review) => {
    setCompleting(review);
    setText(review.text || '');
    setRating(review.rating || 0);
    setReceiptAmount('');
    setCoinsUsed('');
    setReceiptFile(null);
    setPhotoFile(null);
  };

  // Calculate max coins usable: 4% of pre-bill (Parte 4 documento).
  // prezzo_originale = importo_scontrino / (1 - 0.04)
  const getMaxCoins = () => {
    if (!receiptAmount || parseFloat(receiptAmount) <= 0 || walletBalance <= 0) return 0;
    const importo = parseFloat(receiptAmount);
    const prezzoOriginale = importo / 0.96;
    const maxDiscount = prezzoOriginale * 0.04;
    return Math.min(Math.floor(maxDiscount * 100) / 100, walletBalance);
  };

  // Detects the Phase-20 inactive-referral error and routes it to the UX dialog.
  // Returns true if handled (caller must NOT also show a generic toast).
  const handleInactiveReferralError = (err, review, source) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'REFERRAL_USER_INACTIVE') {
      setInactiveReferral({ review, source });
      return true;
    }
    return false;
  };

  const handleComplete = async () => {
    if (rating < 1 || rating > 5) { toast.error(t('dc_rating_req')); return; }
    if (!text.trim()) { toast.error(t('dc_text')); return; }
    if (!receiptAmount || parseFloat(receiptAmount) <= 0) { toast.error(t('dc_receipt_amount_req')); return; }
    if (!receiptFile) { toast.error(t('dc_receipt_req')); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('text', text);
      formData.append('rating', String(rating));
      formData.append('receipt_amount', receiptAmount);
      formData.append('coins_used', coinsUsed || '0');
      if (receiptFile) formData.append('receipt_image', receiptFile);
      if (photoFile) formData.append('personal_photo', photoFile);
      await axios.put(`${API}/drafts/${completing.review_id}/complete`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Review inviata per validazione!');
      setCompleting(null);
      fetchReviews();
    } catch (e) {
      if (handleInactiveReferralError(e, completing, 'complete')) {
        // Keep the complete dialog open so user data is preserved; show the inactive dialog on top.
      } else {
        const d = e.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : (d?.message || 'Error'));
      }
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></div>
  );

  const drafts = reviews.filter(r => r.status === 'SAVED');
  const submitted = reviews.filter(r => r.status === 'IN_VALIDATION');
  const correction = reviews.filter(r => r.status === 'CORRECTION_REQUIRED');
  const validated = reviews.filter(r => r.status === 'VALIDATED');
  const expired = reviews.filter(r => r.status === 'EXPIRED');

  const openCorrection = (r) => {
    setCorrecting(r);
    setCorrectText(r.text || '');
    setCorrectRating(r.rating || 0);
    setCorrectAmount(r.receipt_amount ? String(r.receipt_amount) : '');
    setCorrectCoins(r.coins_used ? String(r.coins_used) : '');
  };

  const openEditDraft = (r) => {
    setEditingDraft(r);
    setEditText(r.text || '');
    setEditRating(r.rating || 0);
  };

  const submitEditDraft = async () => {
    if (!editingDraft) return;
    if (!editText.trim() && editRating === 0) {
      toast.error(t('ed_empty_error'));
      return;
    }
    setEditingBusy(true);
    try {
      await axios.patch(`${API}/drafts/${editingDraft.review_id}`, {
        text: editText,
        rating: editRating,
      }, { withCredentials: true });
      toast.success(t('ed_saved_ok'));
      setEditingDraft(null);
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setEditingBusy(false); }
  };

  const submitCorrection = async () => {
    if (!correcting) return;
    if (correctRating < 1 || correctRating > 5) { toast.error(t('dc_rating_req')); return; }
    if (!correctText.trim()) { toast.error(t('dc_text')); return; }
    if (!correctAmount || parseFloat(correctAmount) <= 0) { toast.error(t('dc_receipt_amount_req')); return; }
    setCorrectingBusy(true);
    try {
      await axios.put(`${API}/reviews/${correcting.review_id}/correct`, {
        text: correctText,
        rating: correctRating,
        receipt_amount: parseFloat(correctAmount),
        coins_used: parseFloat(correctCoins || '0'),
      }, { withCredentials: true });
      toast.success(t('corr_success'));
      setCorrecting(null);
      fetchReviews();
    } catch (e) {
      if (handleInactiveReferralError(e, correcting, 'correct')) {
        // Keep correction dialog open
      } else {
        const d = e.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : (d?.message || 'Error'));
      }
    } finally { setCorrectingBusy(false); }
  };

  // Phase 20 — actions from the inactive-referral dialog
  const inactiveRefBackToEdit = () => {
    setInactiveReferral(null);
    // Both complete and correct dialogs remain open — nothing to do
  };
  const inactiveRefChooseNew = () => {
    const ref = inactiveReferral;
    setInactiveReferral(null);
    setCompleting(null);
    setCorrecting(null);
    // Jump to restaurant detail with swap query param — RestaurantDetail will PATCH on select.
    navigate(`/restaurants/${ref.review.restaurant_id}?swap_review_id=${ref.review.review_id}`);
  };
  const inactiveRefCancelReview = async () => {
    const ref = inactiveReferral;
    if (!ref) return;
    try {
      await axios.delete(`${API}/drafts/${ref.review.review_id}`, { withCredentials: true });
      toast.success(t('mr_deleted') || 'Recensione annullata');
    } catch (e) {
      // If DELETE is not allowed on this status, inform the user clearly
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Error'));
    }
    setInactiveReferral(null);
    setCompleting(null);
    setCorrecting(null);
    fetchReviews();
  };

  const renderReviewCard = (review, i) => {
    const isDraft = review.status === 'SAVED';
    const isCorrection = review.status === 'CORRECTION_REQUIRED';
    return (
      <Card key={review.review_id} className="border border-border rounded-2xl card-hover" data-testid={`review-card-${i}`}>
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm font-sans">{review.restaurant_name}</p>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">{new Date(review.created_at).toLocaleDateString()}</p>
            </div>
            <Badge className={`text-xs shrink-0 ${STATUS_STYLES[review.status] || ''}`}>
              {STATUS_ICONS[review.status]} {t(`status_${review.status}`)}
            </Badge>
          </div>

          {/* CORRECTION_REQUIRED banner (Parte 6+13) */}
          {isCorrection && (
            <div className="mb-3 p-3 rounded-lg border border-orange-200 bg-orange-50" data-testid={`correction-banner-${i}`}>
              <div className="flex items-start gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-orange-700 mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="flex-1">
                  <p className="text-xs font-sans font-semibold text-orange-900">{t('corr_banner_title')}</p>
                  <p className="text-[11px] font-sans text-orange-800 mt-0.5"><strong>{t('corr_reason_label')}:</strong> {review.correction_reason || '—'}</p>
                  {review.correction_cycles > 0 && (
                    <p className="text-[10px] font-sans text-orange-700/70 mt-0.5">Ciclo #{review.correction_cycles}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {review.text && <p className="text-sm text-foreground font-sans mb-3 leading-relaxed">{review.text}</p>}
          {review.rating > 0 && (
            <div className="flex items-center gap-1 mb-3">
              {[1,2,3,4,5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-border'}`} strokeWidth={1.5} />)}
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-muted-foreground font-sans mb-3">
            <span>{t('mr_your_code')}: <span className="font-mono font-semibold text-foreground">{review.referral_code}</span></span>
          </div>

          {isDraft && (
            <div className="flex items-center gap-2 flex-wrap">
              {review.expires_at && (
                <span className="text-[11px] text-muted-foreground font-sans">
                  {t('mr_expires')}: {new Date(review.expires_at).toLocaleString()}
                </span>
              )}
              <div className="flex-1" />
              <Button size="sm" variant="outline" onClick={() => handleDelete(review.review_id)} className="rounded-full text-xs text-destructive" data-testid={`delete-draft-${i}`}>
                <Trash2 className="w-3 h-3 mr-1" /> {t('mr_delete')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => openEditDraft(review)} className="rounded-full text-xs" data-testid={`edit-draft-${i}`}>
                <Pencil className="w-3 h-3 mr-1" /> {t('ed_edit')}
              </Button>
              <Button size="sm" onClick={() => openComplete(review)} className="rounded-full text-xs btn-lift" data-testid={`complete-draft-${i}`}>
                <FileText className="w-3 h-3 mr-1" /> {t('mr_complete')}
              </Button>
            </div>
          )}

          {isCorrection && (
            <div className="flex items-center justify-end">
              <Button size="sm" onClick={() => openCorrection(review)} className="rounded-full text-xs btn-lift bg-orange-600 hover:bg-orange-700" data-testid={`reopen-review-${i}`}>
                <RotateCcw className="w-3 h-3 mr-1" strokeWidth={1.5} /> {t('corr_reopen')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="my-reviews-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pt-20 sm:pt-24">
        <div className="mb-6 sm:mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2 font-sans">{t('nav_my_reviews')}</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t('mr_title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1 font-sans">{t('mr_sub')}</p>
        </div>

        {reviews.length === 0 ? (
          <Card className="border border-border rounded-2xl">
            <CardContent className="p-8 text-center">
              <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-muted-foreground font-sans text-sm">{t('mr_no_reviews')}</p>
              <Button onClick={() => navigate('/restaurants')} className="mt-4 rounded-full btn-lift" data-testid="browse-restaurants-btn">
                {t('dash_browse')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {drafts.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-amber-700 mb-3 font-sans">{t('status_SAVED')} ({drafts.length})</p>
                <div className="space-y-3">{drafts.map((r, i) => renderReviewCard(r, `draft-${i}`))}</div>
              </div>
            )}
            {submitted.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 mb-3 font-sans">{t('status_IN_VALIDATION')} ({submitted.length})</p>
                <div className="space-y-3">{submitted.map((r, i) => renderReviewCard(r, `sub-${i}`))}</div>
              </div>
            )}
            {correction.length > 0 && (
              <div className="mt-6" data-testid="correction-group">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-orange-700 mb-3 font-sans">{t('status_CORRECTION_REQUIRED')} ({correction.length})</p>
                <div className="space-y-3">{correction.map((r, i) => renderReviewCard(r, `corr-${i}`))}</div>
              </div>
            )}
            {validated.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-emerald-700 mb-3 font-sans">{t('status_VALIDATED')} ({validated.length})</p>
                <div className="space-y-3">{validated.map((r, i) => renderReviewCard(r, `val-${i}`))}</div>
              </div>
            )}
            {expired.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3 font-sans">{t('status_EXPIRED')} ({expired.length})</p>
                <div className="space-y-3">{expired.map((r, i) => renderReviewCard(r, `exp-${i}`))}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Complete Draft Dialog */}
      <Dialog open={!!completing} onOpenChange={() => setCompleting(null)}>
        <DialogContent className="rounded-2xl max-w-lg mx-4" data-testid="complete-draft-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-xl sm:text-2xl">{t('dc_title')}</DialogTitle>
            <DialogDescription className="font-sans">{t('dc_for')} {completing?.restaurant_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-semibold font-sans block mb-2">{t('dc_rating')} *</label>
              <StarRating value={rating} onChange={setRating} size="lg" />
              {rating === 0 && <p className="text-xs text-muted-foreground mt-1 font-sans">{t('dc_rating_req')}</p>}
            </div>
            <div>
              <label className="text-sm font-semibold font-sans block mb-2">{t('dc_text')} *</label>
              <textarea data-testid="draft-text" value={text} onChange={e => setText(e.target.value)} placeholder={t('dc_text_ph')}
                className="w-full min-h-[100px] rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-sans resize-none focus:outline-none focus:ring-1 focus:ring-ring" maxLength={500} />
              <p className="text-xs text-muted-foreground text-right font-sans">{text.length}/500</p>
            </div>
            <div>
              <label className="text-sm font-semibold font-sans block mb-2">{t('dc_receipt_amount')} *</label>
              <input data-testid="draft-receipt-amount" type="number" step="0.01" min="0" value={receiptAmount} onChange={e => setReceiptAmount(e.target.value)} placeholder="es. 45.50"
                className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-sans h-10 focus:outline-none focus:ring-1 focus:ring-ring" />
            </div>
            <div className={`border rounded-xl p-3 ${walletBalance > 0 && receiptAmount && parseFloat(receiptAmount) > 0 ? 'border-primary/20 bg-primary/5' : 'border-border bg-muted/30'}`}>
              <label className="text-sm font-semibold font-sans block mb-1.5">Coins usate (facoltativo)</label>
              <p className="text-xs text-muted-foreground font-sans mb-2">
                Saldo disponibile: {walletBalance.toFixed(2)} coin {receiptAmount && parseFloat(receiptAmount) > 0 && walletBalance > 0 ? `| Max utilizzabili: ${getMaxCoins().toFixed(2)} coin` : ''}
              </p>
              <input data-testid="draft-coins-used" type="number" step="0.01" min="0" max={getMaxCoins()} value={coinsUsed}
                onChange={e => setCoinsUsed(e.target.value)}
                placeholder={walletBalance > 0 && receiptAmount && parseFloat(receiptAmount) > 0 ? `Max ${getMaxCoins().toFixed(2)}` : 'Nessun coin disponibile'}
                disabled={!(walletBalance > 0 && receiptAmount && parseFloat(receiptAmount) > 0)}
                className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-sans h-10 focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed" />
            </div>
            <div>
              <label className="text-sm font-semibold font-sans block mb-2">{t('dc_receipt')} *</label>
              <div className="dropzone rounded-xl p-4 text-center cursor-pointer" onClick={() => document.getElementById('draft-receipt').click()} data-testid="draft-receipt-dropzone">
                {receiptFile ? <div className="flex items-center gap-2 justify-center"><CheckCircle className="w-4 h-4 text-emerald-600" /><span className="text-sm font-sans">{receiptFile.name}</span></div>
                  : <div className="flex items-center gap-2 justify-center text-muted-foreground"><Upload className="w-4 h-4" strokeWidth={1.5} /><span className="text-sm font-sans">{t('upload')}</span></div>}
                <input id="draft-receipt" type="file" accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif" className="hidden" onChange={e => setReceiptFile(e.target.files[0])} />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold font-sans block mb-2">{t('dc_photo')}</label>
              <div className="dropzone rounded-xl p-4 text-center cursor-pointer" onClick={() => document.getElementById('draft-photo').click()} data-testid="draft-photo-dropzone">
                {photoFile ? <div className="flex items-center gap-2 justify-center"><CheckCircle className="w-4 h-4 text-emerald-600" /><span className="text-sm font-sans">{photoFile.name}</span></div>
                  : <div className="flex items-center gap-2 justify-center text-muted-foreground"><Upload className="w-4 h-4" strokeWidth={1.5} /><span className="text-sm font-sans">{t('upload')}</span></div>}
                <input id="draft-photo" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={e => setPhotoFile(e.target.files[0])} />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCompleting(null)} className="rounded-full" data-testid="cancel-complete-btn">{t('dc_cancel')}</Button>
            <Button onClick={handleComplete} disabled={submitting || rating === 0 || !text.trim() || !receiptAmount || !receiptFile} className="rounded-full btn-lift" data-testid="submit-complete-btn">
              {submitting ? t('dc_submitting') : t('dc_submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Correction Dialog (Parte 6+13) */}
      <Dialog open={!!correcting} onOpenChange={() => setCorrecting(null)}>
        <DialogContent className="rounded-2xl max-w-lg mx-4" data-testid="correction-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-xl sm:text-2xl flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-orange-600" strokeWidth={1.5} />
              {t('corr_reopen')}
            </DialogTitle>
            <DialogDescription className="font-sans">
              {correcting?.restaurant_name} — <span className="text-orange-700">{correcting?.correction_reason || ''}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-semibold font-sans block mb-2">{t('dc_rating')} *</label>
              <StarRating value={correctRating} onChange={setCorrectRating} size="lg" />
            </div>
            <div>
              <label className="text-sm font-semibold font-sans block mb-2">{t('dc_text')} *</label>
              <textarea data-testid="correct-text" value={correctText} onChange={e => setCorrectText(e.target.value)} placeholder={t('dc_text_ph')}
                className="w-full min-h-[100px] rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-sans resize-none focus:outline-none focus:ring-1 focus:ring-ring" maxLength={500} />
              <p className="text-xs text-muted-foreground text-right font-sans">{correctText.length}/500</p>
            </div>
            <div>
              <label className="text-sm font-semibold font-sans block mb-2">{t('corr_amount')} *</label>
              <input data-testid="correct-amount" type="number" step="0.01" min="0" value={correctAmount} onChange={e => setCorrectAmount(e.target.value)}
                className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-sans h-10 focus:outline-none focus:ring-1 focus:ring-ring" />
            </div>
            <div className="border border-border rounded-xl p-3 bg-muted/30">
              <label className="text-sm font-semibold font-sans block mb-1.5">{t('corr_coins')}</label>
              <p className="text-xs text-muted-foreground font-sans mb-2">{t('corr_max_coin_hint')}</p>
              <input data-testid="correct-coins" type="number" step="0.01" min="0" value={correctCoins} onChange={e => setCorrectCoins(e.target.value)}
                className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-sans h-10 focus:outline-none focus:ring-1 focus:ring-ring" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCorrecting(null)} className="rounded-full" data-testid="correct-cancel-btn">{t('dc_cancel')}</Button>
            <Button onClick={submitCorrection} disabled={correcting_busy || correctRating === 0 || !correctText.trim() || !correctAmount} className="rounded-full btn-lift bg-orange-600 hover:bg-orange-700" data-testid="correct-submit-btn">
              <RotateCcw className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {correcting_busy ? '...' : t('corr_resubmit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit SAVED Draft Dialog */}
      <Dialog open={!!editingDraft} onOpenChange={() => setEditingDraft(null)}>
        <DialogContent className="rounded-2xl max-w-lg mx-4" data-testid="edit-draft-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-xl sm:text-2xl flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" strokeWidth={1.5} />
              {t('ed_title')}
            </DialogTitle>
            <DialogDescription className="font-sans">
              {editingDraft?.restaurant_name} — {t('ed_sub')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-semibold font-sans block mb-2">{t('dc_rating')}</label>
              <StarRating value={editRating} onChange={setEditRating} size="lg" />
            </div>
            <div>
              <label className="text-sm font-semibold font-sans block mb-2">{t('dc_text')}</label>
              <textarea data-testid="edit-draft-text" value={editText} onChange={e => setEditText(e.target.value)} placeholder={t('dc_text_ph')}
                className="w-full min-h-[100px] rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-sans resize-none focus:outline-none focus:ring-1 focus:ring-ring" maxLength={500} />
              <p className="text-xs text-muted-foreground text-right font-sans">{editText.length}/500</p>
            </div>
            <p className="text-xs text-muted-foreground font-sans">{t('ed_note')}</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingDraft(null)} className="rounded-full" data-testid="edit-draft-cancel-btn">{t('dc_cancel')}</Button>
            <Button onClick={submitEditDraft} disabled={editingBusy || (!editText.trim() && editRating === 0)} className="rounded-full btn-lift" data-testid="edit-draft-save-btn">
              <Pencil className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {editingBusy ? '...' : t('ed_save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 20 — Inactive referral dialog */}
      <Dialog open={!!inactiveReferral} onOpenChange={(open) => { if (!open) setInactiveReferral(null); }}>
        <DialogContent className="rounded-2xl max-w-md mx-4" data-testid="inactive-referral-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-xl sm:text-2xl flex items-center gap-2 text-orange-700">
              <UserX className="w-5 h-5" strokeWidth={1.5} />
              {t('ir_title')}
            </DialogTitle>
            <DialogDescription className="font-sans whitespace-pre-line">
              {t('ir_body')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={inactiveRefBackToEdit} className="rounded-full" data-testid="ir-back-btn">
              {t('ir_back')}
            </Button>
            <Button variant="outline" onClick={inactiveRefCancelReview} className="rounded-full text-destructive" data-testid="ir-cancel-review-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              {t('ir_cancel')}
            </Button>
            <Button onClick={inactiveRefChooseNew} className="rounded-full btn-lift" data-testid="ir-choose-new-btn">
              <ArrowRight className="w-4 h-4 mr-2" />
              {t('ir_choose_new')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
